import React, { useContext, useState, useEffect, useMemo, useRef } from 'react';

import { v4 as uuid } from 'uuid';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { FiChevronDown } from 'react-icons/fi';
import { IoClose } from 'react-icons/io5';
import isEqual from 'lodash/isEqual';

import { AutocompleteProps } from '../../types';
import { AppTextField } from '../inputs';

import { AppChip } from '../AppChip';
import { DeviceContext } from '../../contexts';

const filter = createFilterOptions<any>();

export const appSmallInputStyles = {
  padding: '2px 24px 6px 0px !important',
  maxHeight: '41px !important',
};

export const appAutocompleteUseStyles = makeStyles<Theme, { withoutLabel?: boolean }>(theme =>
  createStyles({
    inputRoot: props => (props.withoutLabel ? appSmallInputStyles : {}),
    input: {
      padding: '8px 8px 8px 15px !important',
      marginTop: 0,
    },
    tag: {
      display: 'none',
    },
    option: {
      [theme.breakpoints.down('xs')]: {
        fontSize: '12px',
        minHeight: '40px',
      },
    },
    popupIndicator: {
      padding: theme.spacing(1),
    },
    clearIndicator: {
      padding: theme.spacing(1),
    },
  })
);

export const ControlledAutocomplete = <T,>({
  name,
  items = [],
  multiple,
  label,
  rules,
  disabled = false,
  disableClearable,
  defaultValue,
  margin,
  onChange,
  renderCustomChip,
  selectedItems,
  isTags = true,
  ...rest
}: AutocompleteProps<T> & {
  renderCustomChip?: (item: T, field: any, deleteItem: (field: any, item: T) => void) => React.ReactNode;
  selectedItems?: T[];
}) => {
  const classes = appAutocompleteUseStyles({ withoutLabel: !label });
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();
  const { isMobile } = useContext(DeviceContext);
  const [key, setKey] = useState(uuid());
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const value = watch(name);

  const errorMessage = errors[name] && errors[name]?.message;

  const deleteItem = (field: any, item: any) => {
    const updatedData = (field.value as Array<any>).filter(value => !isEqual(value, item));
    field.onChange(updatedData);
  };

  const setInputValue = (newInputValue: string) => {
    onChange && onChange(newInputValue);
  };

  const textFieldValue = useMemo(() => {
    if (multiple && !isTags && Array.isArray(value)) {
      return value.map((item: T) => (item as any).name || item).join(', ');
    }
    return value;
  }, [value, multiple, isTags]);

  const [searchValue, setSearchValue] = useState('');

  const inputValue = useMemo(() => {
    if (!isFocused && textFieldValue) {
      return textFieldValue;
    }
    return searchValue;
  }, [isFocused, textFieldValue, searchValue]);

  useEffect(() => {
    if (!value) {
      setKey(uuid());
    }
  }, [value]);

  return (
    <Controller
      render={({ field }: any) => {
        const filteredOptions =
          multiple && Array.isArray(field.value) && isTags
            ? items.filter(item => !field.value.some((selected: any) => isEqual(selected, item)))
            : items;

        return (
          <div className="multiple-autocomplete">
            <Autocomplete
              value={field.value}
              key={key}
              onChange={(event, value) => {
                field.onChange(value);
                if (!multiple) {
                  inputRef.current?.blur();
                }
              }}
              multiple={multiple}
              fullWidth
              popupIcon={<FiChevronDown size="25px" color="#A0A3BD" />}
              closeIcon={<IoClose size="25px" color="#A0A3BD" />}
              disabled={disabled}
              defaultValue={defaultValue}
              disableClearable={disableClearable}
              disableCloseOnSelect={multiple}
              disablePortal={isMobile}
              id={name}
              classes={classes}
              options={filteredOptions}
              onInputChange={(event, newInputValue) => {
                setSearchValue(newInputValue);
                setInputValue(newInputValue);
              }}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              getOptionLabel={option => option.name || option}
              filterOptions={(options, params) => filter(options, params)}
              renderInput={params => {
                return (
                  <AppTextField
                    {...params}
                    inputRef={inputRef}
                    error={errors && !!errorMessage}
                    margin={margin}
                    autoComplete="off"
                    label={label}
                    helperText={((errors && errorMessage) as string) || ''}
                  />
                );
              }}
              {...rest}
            />
            {multiple && isTags && (
              <Box marginTop="12px">
                {(Array.isArray(field.value) &&
                  (field.value as Array<any>).map((item, index) => {
                    return renderCustomChip ? (
                      renderCustomChip(item, field, deleteItem)
                    ) : (
                      <AppChip
                        style={{ margin: '0 6px 6px 0' }}
                        key={index}
                        label={item.name || item}
                        onDelete={() => deleteItem(field, item)}
                      />
                    );
                  })) ||
                  null}
              </Box>
            )}
          </div>
        );
      }}
      control={control}
      name={name}
      rules={rules}
    />
  );
};
